import React, { useState, useEffect, useCallback, useRef } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { Confetti } from '../lib/animations';
import { GameHeader, ScoreBadge, MountainRange, SnowParticles } from '../lib/GameVisuals';
import { MountainSnow, CheckCircle, XCircle, Zap, ChevronRight } from 'lucide-react';

/* ─── Tutorial Screen ─── */
function TutorialScreen({ onStart, items }) {
  // Show first category from the actual items
  const firstItem = items[0];
  const sampleCategory = firstItem?.target_category_fr || '';
  const sampleInstruction = firstItem?.target_instruction || '';
  const sampleCorrect = (firstItem?.correct_words || []).slice(0, 3);
  const sampleDistractors = (firstItem?.distractor_words || []).slice(0, 2);

  return (
    <div data-testid="avalanche-tutorial" style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #B8E4F9 0%, #D4EFFC 40%, #E8F4FD 100%)',
      padding: '2rem',
    }}>
      <div style={{ maxWidth: 760, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <MountainSnow size={48} color="#1971C2" />
          <h1 style={{ fontFamily: 'Fredoka', fontSize: '3.2rem', fontWeight: 700, color: '#1A1A2E' }}>
            L'AVALANCHE
          </h1>
        </div>
        <p style={{ fontFamily: 'Nunito', fontSize: '1.5rem', color: '#333', marginBottom: '2rem', fontWeight: 600 }}>
          Words fall from the mountain — catch the right ones!
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          {/* Card 1: Tap correct */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(47,158,68,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <CheckCircle size={40} color="#2F9E44" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#2F9E44', marginBottom: '0.3rem' }}>
              Catch
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              TAP words that match the category before they fall away
            </div>
          </div>

          {/* Card 2: Avoid wrong */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(224,49,49,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <XCircle size={40} color="#E03131" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#E03131', marginBottom: '0.3rem' }}>
              Avoid
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Don't tap wrong words — they cost points!
            </div>
          </div>

          {/* Card 3: Speed bonus */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(245,159,0,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Zap size={40} color="#F59F00" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#F59F00', marginBottom: '0.3rem' }}>
              Be Quick!
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Correct words that fall off the screen count as misses
            </div>
          </div>
        </div>

        {/* Show category example */}
        {sampleCategory && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.05rem', color: '#555', marginBottom: '0.5rem' }}>
              Example category:
            </div>
            <span style={{
              display: 'inline-block', padding: '0.3rem 1rem', borderRadius: '2rem',
              background: '#1971C2', color: 'white', fontWeight: 700, fontSize: '1.2rem',
              fontFamily: 'Fredoka',
            }}>
              {sampleCategory}
            </span>
            {sampleCorrect.length > 0 && (
              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                {sampleCorrect.map((w, i) => (
                  <span key={i} style={{
                    padding: '0.3rem 0.8rem', borderRadius: '0.75rem',
                    background: 'rgba(47,158,68,0.12)', border: '2px solid rgba(47,158,68,0.3)',
                    fontFamily: 'Fredoka', fontSize: '1.1rem', fontWeight: 600, color: '#2F9E44',
                  }}>{w}</span>
                ))}
                {sampleDistractors.map((w, i) => (
                  <span key={`d-${i}`} style={{
                    padding: '0.3rem 0.8rem', borderRadius: '0.75rem',
                    background: 'rgba(224,49,49,0.08)', border: '2px solid rgba(224,49,49,0.2)',
                    fontFamily: 'Fredoka', fontSize: '1.1rem', fontWeight: 600, color: '#E03131',
                    textDecoration: 'line-through', opacity: 0.7,
                  }}>{w}</span>
                ))}
              </div>
            )}
          </div>
        )}

        <button data-testid="avalanche-tutorial-start" onClick={onStart} className="btn-arcade btn-primary"
          style={{ fontSize: '1.8rem', padding: '0 3rem', minHeight: 72, gap: '0.75rem', borderBottomWidth: 8 }}>
          <ChevronRight size={32} /> Let's Go!
        </button>
      </div>
    </div>
  );
}

/* ─── Set Transition Screen ─── */
function SetTransition({ setNumber, totalSets, categoryFr, instruction, onReady }) {
  useEffect(() => {
    const timer = setTimeout(onReady, 2400);
    return () => clearTimeout(timer);
  }, [onReady]);

  return (
    <div data-testid="avalanche-set-transition" style={{
      position: 'absolute', inset: 0, zIndex: 50,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.95)', borderRadius: '1.5rem', padding: '2rem 3rem',
        textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        animation: 'popIn 0.3s ease-out',
      }}>
        <style>{`@keyframes popIn { from { transform: scale(0.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.2rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '0.3rem' }}>
          WAVE {setNumber} of {totalSets}
        </div>
        <div style={{
          fontFamily: 'Fredoka', fontSize: '2rem', fontWeight: 700, color: '#1971C2', marginBottom: '0.5rem',
        }}>
          {categoryFr}
        </div>
        <div style={{ fontFamily: 'Nunito', fontSize: '1.2rem', color: '#555', fontWeight: 600 }}>
          {instruction}
        </div>
        <div style={{ marginTop: '1rem' }}>
          <div style={{
            width: 48, height: 48, border: '4px solid #E8EDF4',
            borderTopColor: '#1971C2', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', margin: '0 auto',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    </div>
  );
}

/* ─── Mountain Progress Panel (left side) ─── */
function MountainProgress({ currentSet, totalSets, caughtInSet, totalCorrectInSet, totalCaught, totalMissed }) {
  const setProgress = totalSets > 0 ? currentSet / totalSets : 0;
  const catchRate = (totalCaught + totalMissed) > 0
    ? Math.round((totalCaught / (totalCaught + totalMissed)) * 100)
    : 0;

  return (
    <div data-testid="mountain-progress" style={{
      position: 'absolute', left: 12, top: 12, bottom: 12,
      width: 80, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'flex-end',
      zIndex: 15, pointerEvents: 'none',
    }}>
      {/* Set indicators */}
      <div style={{
        flex: 1, width: 40, position: 'relative',
        background: 'rgba(255,255,255,0.3)', borderRadius: '1rem',
        border: '2px solid rgba(255,255,255,0.5)', overflow: 'hidden',
      }}>
        {/* Fill based on sets completed */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: `${setProgress * 100}%`,
          background: 'linear-gradient(180deg, #74C0FC, #1971C2)',
          borderRadius: '0.8rem',
          transition: 'height 0.6s ease-out',
        }} />
        {/* Set markers */}
        {Array.from({ length: totalSets }).map((_, i) => {
          const pct = ((i + 1) / totalSets) * 100;
          const completed = i < currentSet;
          return (
            <div key={i} style={{
              position: 'absolute', bottom: `${pct}%`, left: '50%', transform: 'translate(-50%, 50%)',
              width: completed ? 16 : 10, height: completed ? 16 : 10,
              borderRadius: '50%',
              background: completed ? '#FFD43B' : 'rgba(255,255,255,0.6)',
              border: completed ? '2px solid #F59F00' : '2px solid rgba(255,255,255,0.3)',
              transition: 'all 0.3s ease',
              boxShadow: completed ? '0 0 8px rgba(255,212,59,0.5)' : 'none',
            }} />
          );
        })}
        {/* Flag at top */}
        <div style={{
          position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)',
          fontSize: '1.2rem', lineHeight: 1,
        }}>
          <MountainSnow size={20} color={setProgress >= 1 ? '#FFD43B' : 'rgba(255,255,255,0.6)'} />
        </div>
      </div>

      {/* Catch rate */}
      <div style={{
        marginTop: 6, textAlign: 'center',
        background: 'rgba(255,255,255,0.85)', borderRadius: '0.5rem',
        padding: '0.2rem 0.4rem', fontSize: '0.75rem', fontWeight: 700,
        fontFamily: 'Fredoka', color: catchRate >= 70 ? '#2F9E44' : catchRate >= 40 ? '#F59F00' : '#E03131',
      }}>
        {catchRate}%
      </div>
    </div>
  );
}

/* ─── Main Game Component ─── */
export function LAvalanche({ items, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('avalanche'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  const containerRef = useRef(null);
  const [showTutorial, setShowTutorial] = useState(true);
  const [currentSet, setCurrentSet] = useState(0);
  const [score, setScore] = useState(0);
  const [misses, setMisses] = useState(0);
  const [wrongTaps, setWrongTaps] = useState(0);
  const [totalTaps, setTotalTaps] = useState(0);
  const [showConfetti, setShowConfetti] = useState(0);
  const [targetCategory, setTargetCategory] = useState('');
  const [targetInstruction, setTargetInstruction] = useState('');
  const [showTransition, setShowTransition] = useState(false);
  const [caughtInSet, setCaughtInSet] = useState(0);
  const [correctInSet, setCorrectInSet] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);

  const scoreRef = useRef(null);
  const missRef = useRef(null);
  const activeWordsRef = useRef([]);
  const gameEndedRef = useRef(false);
  const pendingSetRef = useRef(-1);
  const totalCaughtRef = useRef(0);
  const totalMissedRef = useRef(0);
  const totalWrongRef = useRef(0);
  const totalTapsRef = useRef(0);
  const bestStreakRef = useRef(0);
  const streakRef = useRef(0);
  const setWordTotalRef = useRef(0);     // total words in current set
  const setResolvedRef = useRef(0);      // words resolved (tapped or fallen off) in current set
  const setAdvancingRef = useRef(false);  // prevent double-advance
  const checkSetDoneRef = useRef(null);   // ref to checkSetDone function to avoid stale closure
  const reviewSetsRef = useRef([]);       // track review data per set
  const currentSetWordsRef = useRef([]);  // words in current set for review

  const avalancheItems = items.filter(i => i.correct_words || i.distractor_words);
  const maxSets = Math.min(avalancheItems.length, settings.rounds || 10);

  const clearActiveWords = useCallback(() => {
    activeWordsRef.current.forEach(w => {
      if (w.tween) w.tween.kill();
      if (w.driftTween) w.driftTween.kill();
      if (w.rotTween) w.rotTween.kill();
      if (w.element?.parentNode) w.element.parentNode.removeChild(w.element);
    });
    activeWordsRef.current = [];
  }, []);

  const endGame = useCallback(() => {
    if (gameEndedRef.current) return;
    gameEndedRef.current = true;
    setShowConfetti(c => c + 1);
    playSound('win');
    clearActiveWords();

    // Save last set's review data
    const lastSetIdx = pendingSetRef.current;
    if (currentSetWordsRef.current.length > 0) {
      const lastItem = avalancheItems[lastSetIdx];
      reviewSetsRef.current.push({
        setLabel: `Wave ${lastSetIdx + 1}`,
        setTitle: lastItem?.target_category_fr || '',
        words: [...currentSetWordsRef.current],
      });
    }
    // Add any unanswered sets
    for (let i = reviewSetsRef.current.length; i < maxSets && i < avalancheItems.length; i++) {
      const it = avalancheItems[i];
      if (!it) break;
      const allWords = [
        ...(it.correct_words || []).map(w => ({ text: w, isCorrect: true, wasCorrect: null, correctCategory: it.target_category_fr })),
        ...(it.distractor_words || []).map(w => ({ text: w, isCorrect: false, wasCorrect: null })),
      ];
      reviewSetsRef.current.push({ setLabel: `Wave ${i + 1}`, setTitle: it.target_category_fr || '', words: allWords });
    }

    const caught = totalCaughtRef.current;
    const missed = totalMissedRef.current;
    const best = bestStreakRef.current;
    const total = caught + missed;

    setTimeout(() => onGameEnd({
      team1Score: caught, team2Score: 0,
      totalQuestions: total,
      accuracy: total > 0 ? ((caught / total) * 100) : 0,
      bestStreak: best,
      questionReview: reviewSetsRef.current, reviewType: 'category',
    }), 1500);
  }, [onGameEnd, playSound, clearActiveWords]);

  // Skip-to-end: parent fires this when teacher hits "Skip" in the game menu
  useEffect(() => {
    const onForceEnd = () => endGame();
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [endGame]);

  const spawnWord = useCallback((word, container, duration) => {
    const el = document.createElement('div');
    const xPos = 8 + Math.random() * 76; // Keep words within 8%-84% to avoid edge clipping
    el.textContent = word.text;
    el.style.cssText = `
      position:absolute; top:-90px; left:${xPos}%; transform:translateX(-50%);
      padding:0.8rem 1.8rem; font-size:2rem; font-weight:700; font-family:'Fredoka',sans-serif;
      color:var(--text-primary); background:rgba(255,255,255,0.94);
      border:3px solid rgba(0,0,0,0.06);
      border-radius:1.5rem; cursor:pointer; touch-action:manipulation; user-select:none;
      white-space:nowrap; z-index:10; pointer-events:auto;
      box-shadow:0 6px 20px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.06);
      backdrop-filter:blur(4px);
      min-width:80px; text-align:center;
    `;
    container.appendChild(el);

    const wordObj = { element: el, isCorrect: word.isCorrect, tapped: false };

    const handleTap = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (wordObj.tapped || gameEndedRef.current) return;
      wordObj.tapped = true;
      totalTapsRef.current += 1;
      setTotalTaps(t => t + 1);

      if (word.isCorrect) {
        playSound('correct');
        totalCaughtRef.current += 1;
        setScore(s => s + 1);
        setCaughtInSet(c => c + 1);
        streakRef.current += 1;
        if (streakRef.current > bestStreakRef.current) bestStreakRef.current = streakRef.current;
        setStreak(streakRef.current);
        setBestStreak(bestStreakRef.current);
        if (scoreRef.current) gsap.fromTo(scoreRef.current, { scale: 1.3 }, { scale: 1, duration: 0.3, ease: 'elastic.out(1,0.5)' });
        // Track for review
        currentSetWordsRef.current.push({ text: word.text, isCorrect: true, wasCorrect: true });
        gsap.to(el, {
          scale: 1.2, opacity: 0,
          background: '#2F9E44', borderColor: '#2F9E44', color: 'white',
          duration: 0.35,
          onComplete: () => { if (el.parentNode) el.parentNode.removeChild(el); if (checkSetDoneRef.current) checkSetDoneRef.current(); },
        });
      } else {
        playSound('wrong');
        totalWrongRef.current += 1;
        setWrongTaps(w => w + 1);
        streakRef.current = 0;
        setStreak(0);
        // Track wrong tap for review
        currentSetWordsRef.current.push({ text: word.text, isCorrect: false, wasCorrect: false });
        gsap.to(el, { x: 10, duration: 0.05, repeat: 5, yoyo: true });
        gsap.to(el, {
          background: '#E03131', borderColor: '#E03131', color: 'white',
          scale: 0.85, opacity: 0,
          duration: 0.4, delay: 0.3,
          onComplete: () => { if (el.parentNode) el.parentNode.removeChild(el); if (checkSetDoneRef.current) checkSetDoneRef.current(); },
        });
      }
    };
    el.addEventListener('pointerdown', handleTap);

    const tween = gsap.to(el, {
      y: container.offsetHeight + 100,
      duration: duration + Math.random() * 2,
      ease: 'none',
      onComplete: () => {
        if (!wordObj.tapped && word.isCorrect) {
          // Missed correct word — flash feedback
          totalMissedRef.current += 1;
          setMisses(m => m + 1);
          streakRef.current = 0;
          setStreak(0);
          // Track missed for review
          currentSetWordsRef.current.push({ text: word.text, isCorrect: true, wasCorrect: false });
          if (missRef.current) gsap.fromTo(missRef.current, { scale: 1.3 }, { scale: 1, duration: 0.3, ease: 'elastic.out(1,0.5)' });
          // Brief red flash at bottom of screen
          const flash = document.createElement('div');
          flash.style.cssText = `
            position:absolute; bottom:0; left:0; right:0; height:6px;
            background:#E03131; z-index:20;
          `;
          container.appendChild(flash);
          gsap.to(flash, { opacity: 0, duration: 0.6, onComplete: () => { if (flash.parentNode) flash.parentNode.removeChild(flash); } });
        }
        if (el.parentNode) el.parentNode.removeChild(el);
        if (!wordObj.tapped && checkSetDoneRef.current) checkSetDoneRef.current(); // Untapped word fell off — resolve it
      },
    });
    const driftTween = gsap.to(el, { x: `+=${(Math.random() - 0.5) * 60}`, duration, ease: 'sine.inOut' });
    const rotTween = gsap.to(el, { rotation: (Math.random() - 0.5) * 15, duration: duration * 0.5, ease: 'sine.inOut', yoyo: true, repeat: -1 });

    wordObj.tween = tween;
    wordObj.driftTween = driftTween;
    wordObj.rotTween = rotTween;
    activeWordsRef.current.push(wordObj);
  }, [playSound]);

  const checkSetDone = useCallback(() => {
    // Count-based: only advance when all spawned words have been resolved
    setResolvedRef.current += 1;
    if (setResolvedRef.current < setWordTotalRef.current || gameEndedRef.current || setAdvancingRef.current) return;
    setAdvancingRef.current = true;
    const nextIdx = pendingSetRef.current + 1;
    if (nextIdx < maxSets) {
      setTimeout(() => {
        if (!gameEndedRef.current) {
          pendingSetRef.current = nextIdx;
          setCurrentSet(nextIdx);
          setShowTransition(true);
          setAdvancingRef.current = false;
        }
      }, 600);
    } else {
      endGame();
    }
  }, [maxSets, endGame]);

  // Keep ref updated with latest checkSetDone
  checkSetDoneRef.current = checkSetDone;

  const startSet = useCallback((setIdx) => {
    const item = avalancheItems[setIdx];
    if (!item) { endGame(); return; }

    // Save previous set's review data (if any)
    if (currentSetWordsRef.current.length > 0 && setIdx > 0) {
      const prevItem = avalancheItems[setIdx - 1];
      reviewSetsRef.current.push({
        setLabel: `Wave ${setIdx}`,
        setTitle: prevItem?.target_category_fr || '',
        words: [...currentSetWordsRef.current],
      });
    }
    currentSetWordsRef.current = [];

    setTargetCategory(item.target_category_fr || '');
    setTargetInstruction(item.target_instruction || 'TAP the correct words!');
    setCaughtInSet(0);
    setCorrectInSet((item.correct_words || []).length);
    pendingSetRef.current = setIdx;

    const correct = (item.correct_words || []).map(w => ({ text: w, isCorrect: true }));
    const distractors = (item.distractor_words || []).map(w => ({ text: w, isCorrect: false }));
    const allWords = [...correct, ...distractors];
    // Fisher-Yates shuffle for uniform randomness
    for (let i = allWords.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allWords[i], allWords[j]] = [allWords[j], allWords[i]];
    }

    const container = containerRef.current;
    if (!container) return;
    clearActiveWords();

    // Reset set-level counters
    setWordTotalRef.current = allWords.length;
    setResolvedRef.current = 0;
    setAdvancingRef.current = false;

    // Word speed: teacher-controlled via slider on setup page (seconds)
    const baseDuration = settings.wordSpeed || 7;

    allWords.forEach((word, i) => {
      const delay = i * (800 + Math.random() * 500);
      setTimeout(() => {
        if (gameEndedRef.current) return;
        spawnWord(word, container, baseDuration);
      }, delay);
    });
  }, [avalancheItems, settings, spawnWord, clearActiveWords, endGame]);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    gameEndedRef.current = false; // Reset game ended flag when starting
    // Show first set transition
    if (avalancheItems.length > 0) {
      setCurrentSet(0);
      setTargetCategory(avalancheItems[0].target_category_fr || '');
      setTargetInstruction(avalancheItems[0].target_instruction || '');
      setShowTransition(true);
    }
  }, [avalancheItems]);

  const handleTransitionReady = useCallback(() => {
    setShowTransition(false);
    // Small delay to ensure container is ready after transition hides
    setTimeout(() => {
      startSet(currentSet);
    }, 50);
  }, [currentSet, startSet]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      gameEndedRef.current = true;
      // Clear active words on unmount
      activeWordsRef.current.forEach(w => {
        if (w.tween) w.tween.kill();
        if (w.driftTween) w.driftTween.kill();
        if (w.rotTween) w.rotTween.kill();
        if (w.element?.parentNode) w.element.parentNode.removeChild(w.element);
      });
      activeWordsRef.current = [];
    };
  }, []); // Empty deps - only run on unmount

  if (showTutorial) {
    return <TutorialScreen onStart={handleTutorialStart} items={avalancheItems} />;
  }

  return (
    <div data-testid="avalanche-game" style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: 'var(--bg)', overflow: 'hidden',
    }}>
      <Confetti trigger={showConfetti} />

      <GameHeader title="L'AVALANCHE" icon={<MountainSnow size={28} color="#1971C2" />} color="#1971C2">
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{
            fontSize: '1.15rem', color: 'var(--accent)', fontWeight: 700,
            fontFamily: 'Fredoka', lineHeight: 1.2,
          }}>{targetInstruction}</div>
          {targetCategory && (
            <span style={{
              display: 'inline-block', padding: '0.15rem 0.85rem', borderRadius: '2rem',
              background: '#1971C2', color: 'white', fontWeight: 700, fontSize: '1.1rem',
              fontFamily: 'Fredoka', marginTop: '0.2rem',
            }}>{targetCategory}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            padding: '0.2rem 0.6rem', borderRadius: '0.5rem',
            background: 'rgba(25,113,194,0.1)', fontFamily: 'Fredoka',
            fontSize: '1rem', fontWeight: 700, color: '#1971C2',
          }}>
            Wave {currentSet + 1}/{maxSets}
          </div>
          {streak >= 3 && (
            <div style={{
              padding: '0.2rem 0.5rem', borderRadius: '0.5rem',
              background: 'rgba(245,159,0,0.12)', fontFamily: 'Fredoka',
              fontSize: '0.95rem', fontWeight: 700, color: '#F59F00',
              display: 'flex', alignItems: 'center', gap: '0.2rem',
            }}>
              <Zap size={14} /> {streak}
            </div>
          )}
        </div>
        <ScoreBadge label="CAUGHT" value={score} color="var(--success)" scoreRef={scoreRef} />
        <ScoreBadge label="MISSED" value={misses} color="var(--error)" scoreRef={missRef} />
      </GameHeader>

      {/* Catch progress bar for current set */}
      {correctInSet > 0 && (
        <div data-testid="set-progress-bar" style={{
          height: 6, background: 'rgba(0,0,0,0.06)',
          position: 'relative', zIndex: 15,
        }}>
          <div style={{
            height: '100%', background: 'linear-gradient(90deg, #2F9E44, #40C057)',
            width: `${Math.min(100, (caughtInSet / correctInSet) * 100)}%`,
            transition: 'width 0.3s ease-out', borderRadius: '0 3px 3px 0',
          }} />
        </div>
      )}

      {/* Game area */}
      <div ref={containerRef} style={{
        flex: 1, position: 'relative', overflow: 'hidden',
      }}>
        {/* Mountain scene background */}
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          viewBox="0 0 1200 600" preserveAspectRatio="none">
          <defs>
            <linearGradient id="aval-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#B8E4F9" />
              <stop offset="40%" stopColor="#D4EFFC" />
              <stop offset="100%" stopColor="#E8F4FD" />
            </linearGradient>
          </defs>
          <rect width="1200" height="600" fill="url(#aval-sky)" />
          <MountainRange width={1200} baseY={200} peaks={6} maxHeight={160} color="#8FADC4" snowLine={0.3} />
          <MountainRange width={1200} baseY={280} peaks={5} maxHeight={100} color="#A8C5D8" snowLine={0.4} />
          <path d="M0,350 Q300,340 600,355 Q900,345 1200,350 L1200,600 L0,600 Z" fill="#E8EDF4" />
          <path d="M0,380 Q300,370 600,385 Q900,375 1200,380 L1200,600 L0,600 Z" fill="#F0F4F8" />
        </svg>

        <SnowParticles count={20} />

        {/* Mountain progress panel */}
        <MountainProgress
          currentSet={currentSet}
          totalSets={maxSets}
          caughtInSet={caughtInSet}
          totalCorrectInSet={correctInSet}
          totalCaught={score}
          totalMissed={misses}
        />

        {/* Set transition overlay */}
        {showTransition && (
          <SetTransition
            setNumber={currentSet + 1}
            totalSets={maxSets}
            categoryFr={targetCategory}
            instruction={targetInstruction}
            onReady={handleTransitionReady}
          />
        )}
      </div>
    </div>
  );
}
